import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useCheckActivationTokenQuery } from "../../../hooks/queries/useAuthQueries";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import { CustomLoadingOverlay } from "../../ui/CustomLoadingOverlay/CustomLoadingOverLay";

const TOKEN_ERROR_ROUTES = {
  used: { to: "/auth/login", reason: "already_used" },
  expired: { to: "/auth/request-reactivation", reason: "expired" },
};

const FALLBACK_ROUTE = { to: "/auth/request-reactivation", reason: "invalid" };

const classifyTokenError = (error) => {
  const msg = extractAxiosErrorMessage(error).toLowerCase();
  return TOKEN_ERROR_ROUTES[msg] ?? FALLBACK_ROUTE;
};

const TokenValidatorGuard = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const { isLoading, isSuccess, isError, error } =
    useCheckActivationTokenQuery(token);

  useEffect(() => {
    if (!token) {
      navigate("/auth/request-reactivation", { replace: true, state: { bannerReason: "invalid" } });
      return;
    }
    if (isError) {
      const { to, reason } = classifyTokenError(error);
      navigate(to, { replace: true, state: { bannerReason: reason } });
    }
  }, [token, isError, error, navigate]);

  if (!token || isError) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0">
        <CustomLoadingOverlay
          visible
          overlayProps={{ radius: "sm", blur: 2, backgroundOpacity: 0.6 }}
          loaderProps={{ color: "primary", type: "bars" }}
        />
      </div>
    );
  }

  if (isSuccess) {
    return children;
  }

  return null;
};

export default TokenValidatorGuard;
